/**
 * The layers an applicant only sometimes carries, plus arrival day.
 *
 * Immigration · sponsorship · accommodation · arrival steps. Each section
 * hides itself when it does not apply, so a local day student's record stays
 * as short as it should be.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plane, HandCoins, BedDouble, CheckCircle2 } from 'lucide-react';

const pretty = (s) => String(s || '').replace(/_/g, ' ');
const fail = (e, fallback) => toast.error(e.response?.data?.detail || fallback);

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="border-t pt-3" data-testid={testid}>
    <p className="font-semibold mb-1 flex items-center gap-1 text-sm">
      <Icon className="h-4 w-4 text-indigo-600" /> {title}
    </p>
    {children}
  </div>
);

const Immigration = ({ tenantId, applicationId }) => {
  const qc = useQueryClient();
  const key = ['admissions-immigration', tenantId, applicationId];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/immigration`)).data,
  });
  const move = useMutation({
    mutationFn: (state) => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/immigration`,
      { state }),
    onSuccess: () => { toast.success('Immigration updated'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => fail(e, 'Could not update immigration'),
  });

  if (isLoading) return <Skeleton className="h-16 w-full" />;
  if (!data?.applicable) return null;

  return (
    <Section icon={Plane} title="Immigration" testid="immigration-section">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Badge className={data.cleared ? 'bg-emerald-100 text-emerald-700'
          : 'bg-amber-100 text-amber-800'} data-testid="immigration-state">
          {pretty(data.state)}
        </Badge>
        {data.blocks_registration && !data.cleared && (
          <span className="text-amber-700">Registration is held until this clears</span>
        )}
      </div>
      {!!data.outstanding?.length && (
        <p className="text-xs text-slate-500 mt-1">
          Outstanding: {data.outstanding.join(', ')}
        </p>
      )}
      <div className="flex gap-1 flex-wrap mt-2">
        {data.states.filter((s) => s !== 'not_applicable').map((s) => (
          <button key={s} onClick={() => move.mutate(s)} disabled={move.isPending}
                  data-testid={`immigration-set-${s}`}
                  className="text-[11px] rounded border px-2 py-0.5 capitalize text-slate-600
                             hover:border-slate-900 hover:text-slate-900 transition-colors">
            {pretty(s)}
          </button>
        ))}
      </div>
    </Section>
  );
};

const Sponsorship = ({ tenantId, applicationId }) => {
  const qc = useQueryClient();
  const key = ['admissions-sponsorship', tenantId, applicationId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/sponsorship`)).data,
  });
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (data && !form) {
      setForm({
        type: data.type || 'self', sponsor_name: data.sponsor_name || '',
        contact_email: data.contact_email || '', contact_phone: data.contact_phone || '',
        amount: data.amount || 0, percent: data.percent || 0,
        currency: data.currency || 'USD', commitment_ref: data.commitment_ref || '',
        status: data.status || 'pending', note: data.note || '',
      });
    }
  }, [data, form]);

  const save = useMutation({
    mutationFn: (body) => api.put(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/sponsorship`, body),
    onSuccess: () => { toast.success('Sponsorship saved'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => fail(e, 'Could not save the sponsorship'),
  });

  if (!data || !form) return null;
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <Section icon={HandCoins} title="Sponsorship" testid="sponsorship-section">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div>
          <Label className="text-[11px]">Sponsor type</Label>
          <select value={form.type} onChange={(e) => set({ type: e.target.value })}
                  data-testid="sponsorship-type"
                  className="w-full h-8 rounded border text-xs px-2 capitalize">
            {data.types.map((t) => <option key={t} value={t}>{pretty(t)}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">Sponsor</Label>
          <Input className="h-8 text-xs" value={form.sponsor_name}
                 data-testid="sponsorship-name"
                 onChange={(e) => set({ sponsor_name: e.target.value })} />
        </div>
        <div>
          <Label className="text-[11px]">Status</Label>
          <select value={form.status} onChange={(e) => set({ status: e.target.value })}
                  data-testid="sponsorship-status"
                  className="w-full h-8 rounded border text-xs px-2 capitalize">
            {data.statuses.map((s) => <option key={s} value={s}>{pretty(s)}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">Amount</Label>
          <Input className="h-8 text-xs" type="number" value={form.amount}
                 data-testid="sponsorship-amount"
                 onChange={(e) => set({ amount: Number(e.target.value) })} />
        </div>
        <div>
          <Label className="text-[11px]">Percent covered</Label>
          <Input className="h-8 text-xs" type="number" value={form.percent}
                 data-testid="sponsorship-percent"
                 onChange={(e) => set({ percent: Number(e.target.value) })} />
        </div>
        <div>
          <Label className="text-[11px]">Commitment reference</Label>
          <Input className="h-8 text-xs" value={form.commitment_ref}
                 data-testid="sponsorship-ref"
                 onChange={(e) => set({ commitment_ref: e.target.value })} />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Button size="sm" className="h-7 text-xs" data-testid="sponsorship-save"
                disabled={save.isPending} onClick={() => save.mutate(form)}>
          Save sponsorship
        </Button>
        <span className="text-[11px] text-slate-500">
          Finance stays the source of truth for money received
        </span>
      </div>
    </Section>
  );
};

const Accommodation = ({ tenantId, applicationId, studentId }) => {
  const qc = useQueryClient();
  const key = ['admissions-accommodation', tenantId, applicationId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/accommodation`)).data,
  });
  const { data: rooms } = useQuery({
    queryKey: ['school-housing-rooms', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/housing/rooms`)).data,
    enabled: !!studentId,
  });
  const [roomId, setRoomId] = useState('');

  const request = useMutation({
    mutationFn: (body) => api.put(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/accommodation`, body),
    onSuccess: () => { toast.success('Room request saved'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => fail(e, 'Could not save the request'),
  });
  const allocate = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/accommodation/allocate`,
      { room_id: roomId }),
    onSuccess: () => { toast.success('Room allocated'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => fail(e, 'Could not allocate that room'),
  });

  if (!data) return null;
  return (
    <Section icon={BedDouble} title="Accommodation" testid="accommodation-section">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Badge variant="outline" className="capitalize" data-testid="accommodation-status">
          {pretty(data.request?.status) || 'no request'}
        </Badge>
        {data.allocation && (
          <span data-testid="accommodation-allocation">
            Room {data.allocation.room_number} · {data.allocation.block_name}
          </span>
        )}
        {!data.request && (
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  data-testid="accommodation-request"
                  onClick={() => request.mutate({ status: 'requested' })}>
            Record a room request
          </Button>
        )}
      </div>
      {studentId && !data.allocation && (
        <div className="flex items-center gap-2 mt-2">
          <select value={roomId} onChange={(e) => setRoomId(e.target.value)}
                  data-testid="accommodation-room-select"
                  className="h-8 rounded border text-xs px-2">
            <option value="">Choose a room…</option>
            {(rooms || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.room_number} {r.block_name ? `· ${r.block_name}` : ''}
              </option>
            ))}
          </select>
          <Button size="sm" className="h-7 text-xs" disabled={!roomId || allocate.isPending}
                  data-testid="accommodation-allocate" onClick={() => allocate.mutate()}>
            Allocate through Housing
          </Button>
        </div>
      )}
      {!studentId && (
        <p className="text-[11px] text-slate-500 mt-1">
          A room is allocated once they are registered as a student.
        </p>
      )}
    </Section>
  );
};

const Arrival = ({ tenantId, applicationId, studentId }) => {
  const qc = useQueryClient();
  const key = ['admissions-arrival', tenantId, applicationId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/arrival`)).data,
    enabled: !!studentId,
  });
  const [ticked, setTicked] = useState([]);

  const checkin = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/orientation-checkin`,
      { steps: ticked, issue_qr: true }),
    onSuccess: (res) => {
      toast.success(res.data.qr_issued ? 'Checked in — student QR issued' : 'Checked in');
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['admissions-application', tenantId, applicationId] });
    },
    onError: (e) => fail(e, 'Could not complete arrival'),
  });

  if (!studentId || !data) return null;
  const toggle = (k) => setTicked(ticked.includes(k)
    ? ticked.filter((x) => x !== k) : [...ticked, k]);

  return (
    <Section icon={CheckCircle2} title="Arrival and orientation" testid="arrival-section">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
        {data.steps.map((s) => (
          <label key={s.key} className="flex items-center gap-2 text-xs"
                 data-testid={`arrival-step-${s.key}`}>
            <input type="checkbox" checked={s.done || ticked.includes(s.key)}
                   disabled={s.done} onChange={() => toggle(s.key)} />
            <span className={s.required ? '' : 'text-slate-400'}>{s.label}</span>
            {s.done && <Badge variant="outline" className="text-[10px]">done</Badge>}
          </label>
        ))}
      </div>
      {!!data.outstanding.length && (
        <p className="text-xs text-amber-700 mt-1" data-testid="arrival-outstanding">
          Still needed: {data.outstanding.join(', ')}
        </p>
      )}
      <Button size="sm" className="h-7 text-xs mt-2" data-testid="arrival-checkin"
              disabled={checkin.isPending || data.complete}
              onClick={() => checkin.mutate()}>
        {data.complete ? 'Arrival complete' : 'Complete arrival check-in'}
      </Button>
    </Section>
  );
};

export const AdmissionsJourneyPanel = ({ tenantId, application }) => {
  if (!application?.id) return null;
  return (
    <div className="space-y-3" data-testid="admissions-journey-panel">
      <Immigration tenantId={tenantId} applicationId={application.id} />
      <Sponsorship tenantId={tenantId} applicationId={application.id} />
      <Accommodation tenantId={tenantId} applicationId={application.id}
                     studentId={application.student_id || ''} />
      <Arrival tenantId={tenantId} applicationId={application.id}
               studentId={application.student_id || ''} />
    </div>
  );
};

export const AdmissionsQueues = ({ tenantId, onOpen }) => {
  const { data: immigration } = useQuery({
    queryKey: ['admissions-immigration-queue', tenantId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/immigration-queue`)).data,
  });
  const { data: rooms } = useQuery({
    queryKey: ['admissions-accommodation-queue', tenantId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/accommodation-queue`)).data,
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admissions-queues">
      <div className="rounded-lg border bg-white p-3" data-testid="queue-immigration">
        <p className="font-semibold text-sm flex items-center gap-1 mb-2">
          <Plane className="h-4 w-4 text-indigo-600" /> Immigration
          <span className="text-slate-400 font-normal">
            ({immigration?.items?.length || 0})
          </span>
        </p>
        {!immigration?.items?.length ? (
          <p className="text-xs text-slate-500">Nobody waiting on immigration.</p>
        ) : immigration.items.map((a) => (
          <button key={a.id} onClick={() => onOpen(a.id)}
                  data-testid={`immigration-row-${a.id}`}
                  className="w-full text-left text-xs hover:bg-slate-50 rounded px-2 py-1">
            <span className="font-medium">{a.applicant_name || '—'}</span>
            <span className="text-slate-400"> · {a.application_number}</span>
            <span className="block text-slate-500 capitalize">
              {a.country || '—'} · {pretty(a.state)}
              {a.outstanding?.length ? ` · ${a.outstanding.join(', ')}` : ''}
            </span>
          </button>
        ))}
      </div>

      <div className="rounded-lg border bg-white p-3" data-testid="queue-accommodation">
        <p className="font-semibold text-sm flex items-center gap-1 mb-2">
          <BedDouble className="h-4 w-4 text-indigo-600" /> Room requests
          <span className="text-slate-400 font-normal">({rooms?.items?.length || 0})</span>
        </p>
        {!rooms?.items?.length ? (
          <p className="text-xs text-slate-500">No outstanding room requests.</p>
        ) : rooms.items.map((a) => (
          <button key={a.id} onClick={() => onOpen(a.application_id)}
                  data-testid={`accommodation-row-${a.id}`}
                  className="w-full text-left text-xs hover:bg-slate-50 rounded px-2 py-1">
            <span className="font-medium">{a.applicant_name || '—'}</span>
            <span className="text-slate-400"> · {a.application_number}</span>
            <span className="block text-slate-500 capitalize">
              {pretty(a.status)}{a.needs ? ` · ${a.needs}` : ''}
              {a.student_id ? '' : ' · not yet registered'}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdmissionsJourneyPanel;
